// ═══════════════════════════════════════════════════════════════
// TicketPanel.jsx — Offline support ticket submission and history
// Users create tickets when no agents are online; agents respond async
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { PATHS, TICKET_STATUS, generateId } from './HelpCenterConfig.js';
import { resolveRecordScope, ownerQuery, listRecords } from './recordScope.js';

/**
 * Props:
 *   storage         — StorageAdapter instance
 *   pushAdapter     — PushAdapter instance
 *   user            — { id, name, email, role }
 *   context         — optional { conflictId, page, etc. }
 *   config          — merged HelpCenterConfig
 *   initialMessage  — optional pre-filled message (from ChatPanel offline redirect)
 */
function TicketPanel({ storage, pushAdapter, user, context, config, initialMessage }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingTicket, setViewingTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [showNewForm, setShowNewForm] = useState(!!initialMessage);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(initialMessage || '');
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);
  const unsubTicketRef = useRef(null);
  const unsubMessagesRef = useRef(null);

  const primaryColor = config?.primaryColor || '#d97706';
  const maxLen = config?.maxMessageLength || 2000;
  const msgs = config?.messages || {};

  // ── Load user's tickets ──
  useEffect(() => {
    if (!storage || !user?.id) { setLoading(false); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const scope = await resolveRecordScope(storage, user);
        const all = await listRecords(storage, PATHS.tickets(), ownerQuery(scope));
        if (cancelled) return;
        const mine = (all || [])
          .filter(t => t.userId === user.id)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTickets(mine);
      } catch (e) { /* best effort */ }
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [storage, user?.id]);

  // ── Subscribe to viewed ticket for real-time updates ──
  useEffect(() => {
    if (!storage || !viewingTicket) return;
    const ticketId = viewingTicket._key || viewingTicket.id;

    // Subscribe to ticket metadata
    unsubTicketRef.current = storage.subscribe(PATHS.ticket(ticketId), (data) => {
      if (data) {
        setViewingTicket(prev => ({ ...prev, ...data }));
        // Update in tickets list too
        setTickets(prev => prev.map(t =>
          (t._key || t.id) === ticketId ? { ...t, ...data } : t
        ));
      }
    });

    // Subscribe to ticket messages
    unsubMessagesRef.current = storage.subscribe(PATHS.ticketMessages(ticketId), (data) => {
      if (!data) { setTicketMessages([]); return; }
      const list = Object.entries(data)
        .map(([key, val]) => ({ ...val, _key: key }))
        .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));
      setTicketMessages(list);
    });

    return () => {
      if (unsubTicketRef.current) unsubTicketRef.current();
      if (unsubMessagesRef.current) unsubMessagesRef.current();
    };
  }, [storage, viewingTicket?.id || viewingTicket?._key]);

  // ── Auto-scroll messages ──
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticketMessages.length]);

  // ── Create a new ticket ──
  const handleCreateTicket = async () => {
    if (!storage || !user?.id || !body.trim()) return;
    if (body.trim().length > maxLen) return;
    setSubmitting(true);

    const ticketId = generateId('ticket');
    // Organization stamp lets same-org admins see the ticket; tenant-public goes to support only
    const { tenantId } = await resolveRecordScope(storage, user);
    const ticketRecord = {
      id: ticketId,
      userId: user.id,
      tenantId,
      userName: user.name || 'User',
      userEmail: user.email || null,
      subject: subject.trim() || 'Support Request',
      status: TICKET_STATUS.OPEN,
      context: context || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      assignedTo: null,
      assignedName: null,
    };

    const firstMessage = {
      id: generateId('tmsg'),
      senderId: user.id,
      senderName: user.name || 'User',
      senderRole: 'user',
      text: body.trim(),
      sentAt: new Date().toISOString(),
    };

    try {
      await storage.set(PATHS.ticket(ticketId), ticketRecord);
      await storage.push(PATHS.ticketMessages(ticketId), firstMessage);

      // Notify support agents
      if (pushAdapter) {
        pushAdapter.sendToRole('support', {
          title: 'New Support Ticket',
          body: 'A new support ticket has been submitted.',
          type: 'help-center-ticket',
          data: { ticketId, userId: user.id },
        }).catch(() => {});
      }

      // Add to local list
      const newTicket = { ...ticketRecord, _key: ticketId };
      setTickets(prev => [newTicket, ...prev]);
      setShowNewForm(false);
      setSubject('');
      setBody('');

      // Show confirmation then open the ticket
      setViewingTicket(newTicket);
    } catch (e) {
      console.error('[TicketPanel] Failed to create ticket:', e);
    }
    setSubmitting(false);
  };

  // ── Reply to a ticket ──
  const handleReply = async () => {
    if (!storage || !viewingTicket || !reply.trim()) return;
    if (reply.trim().length > maxLen) return;
    setSending(true);
    const ticketId = viewingTicket._key || viewingTicket.id;

    const msgRecord = {
      id: generateId('tmsg'),
      senderId: user.id,
      senderName: user.name || 'User',
      senderRole: 'user',
      text: reply.trim(),
      sentAt: new Date().toISOString(),
    };

    try {
      await storage.push(PATHS.ticketMessages(ticketId), msgRecord);
      await storage.update(PATHS.ticket(ticketId), { updatedAt: new Date().toISOString() });
      setReply('');

      // Notify assigned agent
      if (viewingTicket.assignedTo && pushAdapter) {
        pushAdapter.sendToUser(viewingTicket.assignedTo, {
          title: 'Ticket Reply',
          body: 'You have a new reply on your support ticket.',
          type: 'help-center-ticket-reply',
          data: { ticketId },
        }).catch(() => {});
      }
    } catch (e) {
      console.error('[TicketPanel] Failed to send reply:', e);
    }
    setSending(false);
  };

  // ── Icons ──
  const TicketIcon = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 0 0-2 2v3a2 2 0 1 1 0 4v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 1 1 0-4V7a2 2 0 0 0-2-2H5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const PlusIcon = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M12 5v14m-7-7h14" strokeLinecap="round" />
    </svg>
  );
  const ArrowLeftIcon = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const SendIcon = () => (
    <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="m22 2-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const statusConfig = {
    [TICKET_STATUS.OPEN]: { label: 'Open', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
    [TICKET_STATUS.IN_PROGRESS]: { label: 'In Progress', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
    [TICKET_STATUS.RESOLVED]: { label: 'Resolved', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  };

  const StatusBadge = ({ status }) => {
    const sc = statusConfig[status] || statusConfig[TICKET_STATUS.OPEN];
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-0.5 rounded-full font-medium ${sc.bg} ${sc.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
        {sc.label}
      </span>
    );
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-stone-200 rounded-full animate-spin" style={{ borderTopColor: primaryColor }} />
      </div>
    );
  }

  // ── New Ticket Form ──
  if (showNewForm) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => { setShowNewForm(false); setBody(''); setSubject(''); }} className="p-1 hover:bg-stone-100 rounded-lg">
            <ArrowLeftIcon />
          </button>
          <h3 className="text-lg font-semibold text-stone-800">New Support Request</h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description of your issue"
            maxLength={200}
            className="w-full p-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-stone-700 placeholder-stone-500"
            style={{ '--tw-ring-color': primaryColor }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Message</label>
          <textarea
            value={body}
            onChange={(e) => e.target.value.length <= maxLen && setBody(e.target.value)}
            placeholder="Describe your issue in detail..."
            rows={5}
            className="w-full p-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent resize-none text-stone-700 placeholder-stone-500"
            style={{ '--tw-ring-color': primaryColor }}
          />
          <p className="text-xs text-stone-500 mt-1 text-right">{body.length}/{maxLen}</p>
        </div>

        {context && (
          <div className="p-3 bg-stone-50 rounded-lg">
            <p className="text-xs text-stone-500">
              Context will be attached: {Object.entries(context).map(([k, v]) => `${k}: ${v}`).join(', ')}
            </p>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={() => { setShowNewForm(false); setBody(''); setSubject(''); }}
            className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateTicket}
            disabled={!body.trim() || submitting}
            className="px-5 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50 hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    );
  }

  // ── Viewing a ticket ──
  if (viewingTicket) {
    const ticketId = viewingTicket._key || viewingTicket.id;
    const isResolved = viewingTicket.status === TICKET_STATUS.RESOLVED;
    const hasNewAgentReply = ticketMessages.some(m => m.senderRole !== 'user' && !m.readByUser);

    return (
      <div className="flex flex-col h-full" style={{ minHeight: '400px' }}>
        {/* Header */}
        <div className="flex items-center gap-3 p-3 border-b border-stone-200 flex-shrink-0">
          <button
            onClick={() => { setViewingTicket(null); setTicketMessages([]); setReply(''); }}
            className="p-1 hover:bg-stone-100 rounded-lg"
          >
            <ArrowLeftIcon />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-stone-800 truncate">
              {viewingTicket.subject || 'Support Request'}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <StatusBadge status={viewingTicket.status} />
              {viewingTicket.assignedName && (
                <span className="text-xs text-stone-500">Assigned to {viewingTicket.assignedName}</span>
              )}
            </div>
          </div>
        </div>

        {/* Ticket created confirmation */}
        {viewingTicket.status === TICKET_STATUS.OPEN && ticketMessages.length <= 1 && (
          <div className="px-4 py-3 bg-green-50 border-b border-green-200">
            <p className="text-sm text-green-700">{msgs.ticketCreated || "We've received your message and will respond as soon as possible."}</p>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Created date */}
          <div className="text-center">
            <span className="text-xs text-stone-600 bg-stone-100 px-3 py-1 rounded-full">
              {new Date(viewingTicket.createdAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>

          {ticketMessages.map((msg) => {
            const isOwn = msg.senderId === user.id;
            const time = new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            return (
              <div key={msg._key || msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} items-end gap-2`}>
                {!isOwn && (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                    {(msg.senderName || 'S')[0].toUpperCase()}
                  </div>
                )}
                <div className="max-w-[75%]">
                  {!isOwn && (
                    <p className="text-xs text-stone-500 mb-0.5 ml-1">{msg.senderName || 'Support'}</p>
                  )}
                  <div
                    className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                      isOwn
                        ? 'rounded-br-sm text-white'
                        : 'rounded-bl-sm bg-stone-100 text-stone-800'
                    }`}
                    style={isOwn ? { backgroundColor: primaryColor } : undefined}
                  >
                    {msg.text}
                  </div>
                  <p className={`text-[10px] text-stone-500 mt-0.5 ${isOwn ? 'text-right' : 'text-left'} px-1`}>{time}</p>
                </div>
              </div>
            );
          })}

          {/* Resolved notice */}
          {isResolved && (
            <div className="text-center pt-2">
              <span className="text-xs text-green-700 bg-green-50 px-3 py-1 rounded-full">
                {msgs.ticketResolved || 'This ticket has been resolved.'}
              </span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Reply compose */}
        {!isResolved && (
          <div className="p-3 border-t border-stone-200 flex-shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                value={reply}
                onChange={(e) => e.target.value.length <= maxLen && setReply(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleReply();
                  }
                }}
                placeholder="Type a reply..."
                rows={1}
                className="flex-1 p-2.5 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent resize-none text-stone-700 placeholder-stone-500 text-sm"
                style={{ '--tw-ring-color': primaryColor, maxHeight: '120px' }}
              />
              <button
                onClick={handleReply}
                disabled={!reply.trim() || sending}
                className="p-2.5 text-white rounded-xl transition-colors disabled:opacity-50 hover:opacity-90 flex-shrink-0"
                style={{ backgroundColor: primaryColor }}
                aria-label="Send reply"
              >
                <SendIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Ticket List ──
  const openTickets = tickets.filter(t => t.status !== TICKET_STATUS.RESOLVED);
  const resolvedTickets = tickets.filter(t => t.status === TICKET_STATUS.RESOLVED);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-stone-800">Support Tickets</h3>
        <button
          onClick={() => setShowNewForm(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-white rounded-lg font-medium text-sm transition-colors hover:opacity-90"
          style={{ backgroundColor: primaryColor }}
        >
          <PlusIcon />
          New Ticket
        </button>
      </div>

      {/* Empty state */}
      {tickets.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 bg-stone-100">
            <TicketIcon />
          </div>
          <p className="text-stone-500 mb-1">No support tickets yet</p>
          <p className="text-sm text-stone-500">Create a ticket when you need help</p>
        </div>
      )}

      {/* Open tickets */}
      {openTickets.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
            Open ({openTickets.length})
          </p>
          <div className="space-y-2">
            {openTickets.map((ticket) => (
              <TicketRow
                key={ticket._key || ticket.id}
                ticket={ticket}
                onClick={() => setViewingTicket(ticket)}
                StatusBadge={StatusBadge}
              />
            ))}
          </div>
        </div>
      )}

      {/* Resolved tickets */}
      {resolvedTickets.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
            Resolved ({resolvedTickets.length})
          </p>
          <div className="space-y-2">
            {resolvedTickets.map((ticket) => (
              <TicketRow
                key={ticket._key || ticket.id}
                ticket={ticket}
                onClick={() => setViewingTicket(ticket)}
                StatusBadge={StatusBadge}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ticket Row Component ──
function TicketRow({ ticket, onClick, StatusBadge }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 rounded-xl transition-colors text-left"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-stone-700 truncate">
          {ticket.subject || 'Support Request'}
        </p>
        <p className="text-xs text-stone-500 mt-0.5">
          {new Date(ticket.updatedAt || ticket.createdAt).toLocaleDateString()} · {ticket.assignedName ? `Assigned to ${ticket.assignedName}` : 'Unassigned'}
        </p>
      </div>
      <div className="flex-shrink-0 ml-3">
        <StatusBadge status={ticket.status} />
      </div>
    </button>
  );
}

export default TicketPanel;
